/* SkillResponsesPanel — teacher-side live view of student answers on a
 * skill-widget quiz that lives on the Interactive Board.
 *
 * Polls /api/live/{classId}/quiz-responses?quiz_id=skill:{widgetId} every
 * 2.5s. Two views: per-student (who picked what, live) and per-question
 * aggregate (how many picked A/B/C/D).
 */
import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Users, BarChart3, ChevronDown, ChevronUp, X, Bell, BellOff } from "lucide-react";
import { useAnswerChime, useChimeMute } from "@/lib/answerChime";

export default function SkillResponsesPanel({ classId, widgets }) {
  const [open, setOpen] = useState(true);
  const [activeId, setActiveId] = useState(widgets[0]?.id || "");
  const [view, setView] = useState("students"); // students | questions
  const [responses, setResponses] = useState([]);
  const widget = widgets.find((w) => w.id === activeId) || widgets[0];
  const [chimeMuted, toggleChime] = useChimeMute();
  useAnswerChime(responses, widget?.id);

  useEffect(() => {
    if (!widgets.some((w) => w.id === activeId) && widgets[0]) setActiveId(widgets[0].id);
  }, [widgets, activeId]);

  useEffect(() => {
    if (!classId || !widget?.id) return;
    let stop = false;
    const tick = async () => {
      try {
        const { data } = await api.get(`/live/${classId}/quiz-responses`, { params: { quiz_id: `skill:${widget.id}` } });
        if (!stop) setResponses(data.responses || []);
      } catch { /* keep polling */ }
    };
    tick();
    const t = setInterval(tick, 2500);
    return () => { stop = true; clearInterval(t); };
  }, [classId, widget?.id]);

  const questions = widget?.questions || [];
  const agg = useMemo(() => questions.map((q, qi) => {
    const counts = (q.options || []).map(() => 0);
    responses.forEach((r) => {
      const a = (r.answers || {})[qi];
      if (a !== undefined && a !== null && counts[a] !== undefined) counts[a] += 1;
    });
    return counts;
  }), [questions, responses]);

  if (!widget) return null;

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        data-testid="skill-responses-show"
        className="fixed bottom-[235px] right-4 z-[80] flex items-center gap-2 bg-[#7C3AED] text-white text-xs font-bold px-3.5 py-2.5 rounded-full shadow-2xl hover:scale-105 transition-transform"
      >
        <Users size={14} /> Live responses · {responses.length}
      </button>
    );
  }

  return (
    <div
      className="fixed bottom-[235px] right-4 z-[80] w-[340px] max-h-[55vh] flex flex-col bg-[#0B0F17] border border-[#7C3AED]/50 rounded-xl shadow-2xl text-white overflow-hidden"
      data-testid="skill-responses-panel"
    >
      <div className="px-3 py-2.5 bg-[#7C3AED] flex items-center gap-2">
        <Users size={13} />
        <div className="flex-1 min-w-0">
          <div className="text-[9px] font-mono uppercase tracking-[0.2em] opacity-80">/ live quiz responses</div>
          <div className="text-xs font-bold truncate">{widget.title}</div>
        </div>
        <button
          onClick={toggleChime}
          data-testid="skill-responses-chime-toggle"
          title={chimeMuted ? "Unmute answer chime" : "Mute answer chime"}
          className={`p-1 rounded hover:bg-white/15 ${chimeMuted ? "opacity-50" : ""}`}
        >{chimeMuted ? <BellOff size={13} /> : <Bell size={13} />}</button>
        <button onClick={() => setOpen(false)} data-testid="skill-responses-hide" className="p-1 rounded hover:bg-white/15"><X size={13} /></button>
      </div>
      {widgets.length > 1 && (
        <select
          value={activeId}
          onChange={(e) => setActiveId(e.target.value)}
          data-testid="skill-responses-widget-select"
          className="mx-3 mt-2 bg-white/5 border border-white/10 rounded-md text-xs px-2 py-1.5 text-white"
        >
          {widgets.map((w) => <option key={w.id} value={w.id} className="bg-[#0B0F17]">{w.title}</option>)}
        </select>
      )}
      <div className="flex gap-1 px-3 pt-2">
        {[{ id: "students", label: "Students", icon: Users }, { id: "questions", label: "By question", icon: BarChart3 }].map((t) => (
          <button
            key={t.id}
            onClick={() => setView(t.id)}
            data-testid={`skill-responses-view-${t.id}`}
            className={`flex-1 inline-flex items-center justify-center gap-1.5 text-[11px] font-semibold py-1.5 rounded-md ${view === t.id ? "bg-[#7C3AED]/30 text-[#C084FC] border border-[#7C3AED]/50" : "text-white/50 border border-white/10 hover:text-white"}`}
          ><t.icon size={11} /> {t.label}</button>
        ))}
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-2">
        {responses.length === 0 && (
          <div className="text-center text-white/40 text-xs py-6">Waiting for students to answer…</div>
        )}
        {view === "students" && responses.map((r) => (
          <StudentRow key={r.student_id} r={r} questions={questions} />
        ))}
        {view === "questions" && responses.length > 0 && questions.map((q, qi) => (
          <div key={qi} className="border border-white/10 rounded-md p-2.5" data-testid={`skill-agg-q${qi}`}>
            <div className="text-[11px] font-semibold leading-snug mb-1.5">Q{qi + 1} · {q.prompt}</div>
            {(q.options || []).map((opt, oi) => {
              const n = agg[qi]?.[oi] || 0;
              const totalAns = (agg[qi] || []).reduce((s, x) => s + x, 0) || 1;
              const isCorrect = oi === (q.answer_index ?? 0);
              return (
                <div key={oi} className="flex items-center gap-1.5 mb-1">
                  <span className={`w-4 h-4 rounded text-[9px] font-bold flex items-center justify-center shrink-0 ${isCorrect ? "bg-emerald-500 text-black" : "bg-white/10 text-white/60"}`}>{String.fromCharCode(65 + oi)}</span>
                  <div className="flex-1 h-3 bg-white/5 rounded overflow-hidden">
                    <div className={`h-full ${isCorrect ? "bg-emerald-500/70" : "bg-[#7C3AED]/60"}`} style={{ width: `${Math.round((n / totalAns) * 100)}%` }} />
                  </div>
                  <span className="text-[10px] font-mono w-4 text-right text-white/70">{n}</span>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

function StudentRow({ r, questions }) {
  const [openRow, setOpenRow] = useState(false);
  const answered = Object.keys(r.answers || {}).length;
  return (
    <div className="border border-white/10 rounded-md" data-testid={`skill-resp-${r.student_id}`}>
      <button onClick={() => setOpenRow((v) => !v)} className="w-full p-2 flex items-center gap-2 text-left">
        <span className="w-7 h-7 rounded-full bg-[#FFD600]/20 text-[#FFD600] flex items-center justify-center text-[10px] font-bold shrink-0">
          {(r.student_name || "?").slice(0, 1).toUpperCase()}
        </span>
        <span className="flex-1 min-w-0">
          <span className="block text-xs font-semibold truncate">{r.student_name}{r.is_guest ? " · guest" : ""}</span>
          <span className="block text-[10px] text-white/50 font-mono">
            {r.status === "final" ? "✓ finished" : `on Q${(r.current_q || 0) + 1}`} · {answered} answered
          </span>
        </span>
        <span className="text-sm font-bold">{r.score}<span className="text-white/40 text-xs">/{r.total}</span></span>
        {openRow ? <ChevronUp size={12} className="text-white/40" /> : <ChevronDown size={12} className="text-white/40" />}
      </button>
      {openRow && (
        <div className="px-2 pb-2 space-y-1">
          {questions.map((q, qi) => {
            const a = (r.answers || {})[qi];
            const has = a !== undefined && a !== null;
            const correct = has && a === (q.answer_index ?? 0);
            return (
              <div key={qi} className="flex items-center gap-1.5 text-[10px]">
                <span className="text-white/40 font-mono w-6 shrink-0">Q{qi + 1}</span>
                {has ? (
                  <span className={`px-1.5 py-0.5 rounded font-semibold ${correct ? "bg-emerald-500/20 text-emerald-300" : "bg-red-500/20 text-red-300"}`}>
                    {String.fromCharCode(65 + a)} · {(q.options || [])[a] ?? ""}
                  </span>
                ) : <span className="text-white/30">—</span>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
